import React, { useContext, useEffect, useState } from 'react';
import { string, bool } from 'prop-types';
import {
  QueryProvider,
  QueryContext,
  params,
  shape,
  extend,
  useDataModel,
  arrayOf,
  bool as boolType,
  string as stringType,
  number as numberType,
} from '@thd-nucleus/data-sources';
import { Typography, RatingMeter } from '@one-thd/sui-atomic-components';
import { useStore } from '@thd-nucleus/experience-context';
import { Image } from '@thd-olt-component-react/image';
import { Price } from '@thd-olt-component-react/price';
import { SwapContext } from '../SwapContext';
import { ProductPodWrapper } from '../elligible-items/product-pod/ProductPodWrapper';

const CurrentItem = ({
  itemId, currentItemSelectable, hideQuickViewMask, openCurrentItemInQuickView, openDetailsInNewTab
}) => {
  const { storeId } = useStore();
  const { setCurrentItem } = useContext(SwapContext);
  const { defaultVariables } = useContext(QueryContext) || {};

  const { data, loading } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    ssr: false,
  });

  useEffect(() => {
    if (!loading && data) {
      setCurrentItem(data?.product);
    }
  }, [data, loading]);

  if (!data) {
    return null;
  }

  const newDefaultVariables = {
    storeId,
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean' && {
      isBrandPricingPolicyCompliant: defaultVariables.current.isBrandPricingPolicyCompliant
    })
  };

  const imageUrl = data?.product?.media?.image?.url;
  const { brandName, productLabel } = data?.product?.identifiers || {};
  const { averageRating, totalReviews } = data?.product?.reviews?.ratingsReviews || {};
  const headerMessage = currentItemSelectable ? 'Most Recent Selection' : 'Current Selection';

  return (
    <div data-testid="current-item">
      <Typography height="tight" variant="h5">{headerMessage}</Typography>

      {currentItemSelectable ? (
        <QueryProvider
          cacheKey="current-item-product-pod-mini"
          defaultVariables={newDefaultVariables}
          dataSource={data.product.dataSource}
        >
          <ProductPodWrapper
            hideKPF
            product={data.product}
            openInQuickViewDescription={openCurrentItemInQuickView}
            openDetailsInNewTab={openDetailsInNewTab}
            hideQuickViewMask={hideQuickViewMask}
          />
        </QueryProvider>
      ) : (
        <div className="sui-row sui-flex sui-px-4 sui-pt-4 sui-pb-6">
          <div className="sui-grid">
            <div className="sui-flex sui-w-20 sui-h-20 sui-pr-2">
              <div className="sui-relative">
                <Image
                  src={imageUrl}
                  alt="Current Selection"
                  title="Current Selection"
                />
              </div>
            </div>
          </div>
          <div className="sui-col sui-w-full sui-pl-2">
            <Typography
              component="span"
              color="primary"
              height="normal"
              weight="bold"
              variant="body-base"
            >{brandName}
            </Typography>
            <Typography
              component="span"
              color="primary"
              height="normal"
              weight="regular"
              variant="body-base"
              lineClamp="3"
            >{productLabel}
            </Typography>
            <div className="sui-flex sui-items-center sui-h-6 sui-mb-3">
              <RatingMeter
                value={parseFloat(averageRating)}
                label={`(${totalReviews})`}
                edge="start"
                RatingProps={{
                  color: 'brand'
                }}
              />
            </div>
            <div className="sui-mb-3">
              <Price
                itemId={itemId}
                storeId={storeId}
                large={false}
                disableRangePricing
                hideBadge
                hideBulkPrice
                hideConditionalPromoDescription
                hideLimitPerOrder
                hidePromotions
                hideSavingsText
                hideRebates
                showPreferredPricingBadge
                showPreferredPricingBadgeToolTip
              />
            </div>
          </div>
        </div>
      )}
      <hr />
    </div>
  );
};

CurrentItem.propTypes = {
  itemId: string.isRequired,
  currentItemSelectable: bool,
  openCurrentItemInQuickView: bool,
  openDetailsInNewTab: bool,
  hideQuickViewMask: bool
};

CurrentItem.defaultProps = {
  currentItemSelectable: false,
  openCurrentItemInQuickView: false,
  openDetailsInNewTab: false,
  hideQuickViewMask: false
};

CurrentItem.displayName = 'CurrentItem';

CurrentItem.dataModel = extend({
  product: params({ itemId: stringType().isRequired() }).shape({
    dataSource: stringType(),
    identifiers: shape({
      brandName: stringType(),
      productLabel: stringType(),
      modelNumber: stringType(),
    }),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: numberType(),
        totalReviews: numberType(),
      }),
    }),
    media: shape({
      image: shape({
        url: stringType(),
      }).client(),
      images: arrayOf({
        subType: stringType(),
        url: stringType(),
        type: stringType(),
        sizes: arrayOf(stringType())
      })
    }),
    pricing: params({ storeId: stringType() }).shape({
      value: numberType({ float: true }),
      mapAboveOriginalPrice: boolType(),
      promotion: shape({
        promoId: stringType(),
        dates: shape({
          start: stringType(),
          end: stringType()
        }),
        promotionTag: stringType(),
      })
    }),
  }),
}, Price, ProductPodWrapper.dataModel.product);

export { CurrentItem };
